import { AgentScope, type CreateStyleEntityInput } from "src/graphql";
import { AGENT_SCOPE_LABELS } from "src/pages/context-hub/context-entities/utils";
import { parseStorageKeyImageLines } from "src/pages/context-hub/components/context-editor/editor-image-urls";

/**
 * A single product surface that reads styles. The list page's Agent scope
 * filter, a new style's starting scope and the "test in" links pick from these.
 */
export type SelectableScope = Extract<AgentScope, "ads" | "lcm">;

// The surfaces a style can be scoped to, in the order they're offered. No
// data-agent path reads styles, so `data` isn't offered.
export const STYLE_SURFACES: SelectableScope[] = [
  AgentScope.Ads,
  AgentScope.Lcm,
];

// Matches the generic entity rule: an empty list means every agent.
export function isStyleVisibleToAgent(
  agentScopes: readonly AgentScope[],
  surface: AgentScope,
): boolean {
  return agentScopes.length === 0 || agentScopes.includes(surface);
}

// The agent pills for a style, worded like a context doc's.
export function getStyleAgentScopeLabels(
  agentScopes: readonly AgentScope[],
): string[] {
  return agentScopes.length === 0
    ? ["All agents"]
    : agentScopes.map((scope) => AGENT_SCOPE_LABELS[scope]);
}

export function buildDuplicateStylePayload(style: {
  name: string;
  description?: string | null;
  markdown: string;
  agentScopes: AgentScope[];
  brandProfiles: Array<{ id: string }>;
}): CreateStyleEntityInput {
  return {
    name: `${style.name} (copy)`,
    description: style.description,
    markdown: style.markdown,
    agentScopes: style.agentScopes,
    // Empty means "applies to all brand profiles", matching the source style.
    brandProfileIds: style.brandProfiles.map((profile) => profile.id),
    // A duplicate is a fresh work-in-progress, so it starts as a draft
    // regardless of whether the source style was published.
    isActive: false,
  };
}

/**
 * The surfaces to list styles for. A single-surface workspace always sees its
 * own surface; otherwise the Agent scope filter decides. `null` means every style.
 */
export function getVisibleStyleScopes({
  canChooseScope,
  isAdStudioEnabled,
  agentScopes,
}: {
  canChooseScope: boolean;
  isAdStudioEnabled: boolean;
  agentScopes: SelectableScope[] | null;
}): SelectableScope[] | null {
  if (!canChooseScope) {
    return [isAdStudioEnabled ? AgentScope.Ads : AgentScope.Lcm];
  }
  return agentScopes;
}

export type StylePreviewImage = {
  id: string;
  src: string;
  alt: string;
};

export type StyleCitedImage = {
  id: string;
  name: string;
  /** Presigned URL for a file target; null for a cited document. */
  url: string | null;
};

/**
 * Card thumbnails, at most `count`: the document's cited images first (in
 * document order), then the storage-key images a pre-migration document still
 * names directly.
 */
export function buildStylePreviewImages({
  cites = [],
  markdown,
  workspaceId,
  count,
}: {
  cites?: StyleCitedImage[];
  markdown?: string | null;
  workspaceId?: number;
  count: number;
}): StylePreviewImage[] {
  const citedImages: StylePreviewImage[] = cites.flatMap((cite) =>
    cite.url ? [{ id: cite.id, src: cite.url, alt: cite.name }] : [],
  );

  const storageKeyImages: StylePreviewImage[] = parseStorageKeyImageLines(
    markdown ?? "",
    workspaceId,
  ).map((image) => ({
    id: `key:${image.storageKey}`,
    src: image.storageKey,
    alt: image.alt,
  }));

  return [...citedImages, ...storageKeyImages].slice(0, count);
}
